import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Search-input state that keeps the box responsive while a heavy list filters/fetches.
 *
 * The problem this solves: when the search value lives in a parent that re-renders a
 * large un-memoized list on every keystroke, the synchronous re-render blocks the input
 * so the next keystroke is dropped ("the input freezes"). Splitting the value fixes it:
 *
 *  - `value`      — updates on every keystroke; bind the <input> to this so it never blocks.
 *  - `debounced`  — trails `value` by `delay` ms; use THIS as the filter/fetch key (and as
 *                   a useMemo dependency) so the heavy list only recomputes once the user pauses.
 *  - `setValue`   — update the instant value (input onChange, or a programmatic set like a scan).
 *  - `flush`      — commit `value` to `debounced` immediately, bypassing the delay (Enter key,
 *                   barcode scan) when the user wants results now.
 *
 * Delay guidance: ~250ms for client-side in-memory filters, ~400ms for server-side calls.
 */
export interface DebouncedSearch {
  value: string;
  debounced: string;
  setValue: (v: string) => void;
  flush: () => void;
}

export function useDebouncedSearch(initial = '', delay = 300): DebouncedSearch {
  const [value, setValueState] = useState(initial);
  const [debounced, setDebounced] = useState(initial);
  // Mirror the latest value in a ref, updated EAGERLY by setValue (not during render),
  // so flush() called synchronously after setValue (e.g. a barcode scan that sets the
  // query then flushes) commits the value just set, not the previous one.
  const valueRef = useRef(value);

  const setValue = useCallback((v: string) => {
    valueRef.current = v;
    setValueState(v);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  const flush = useCallback(() => setDebounced(valueRef.current), []);

  return { value, debounced, setValue, flush };
}
